import { useLanguage } from '../contexts/LanguageContext'

interface KeyGuideProps {
  isVisible?: boolean
  onToggleVisibility?: () => void
  isCameraMinimapVisible?: boolean
}

export function KeyGuide({ isVisible = false, onToggleVisibility, isCameraMinimapVisible = true }: KeyGuideProps) {
  const { t } = useLanguage()

  // Calculate left position based on camera minimap visibility
  // Desktop: 280px when minimap is visible (220px width + 40px gap + 20px margin), 70px when collapsed (40px button width + 30px gap + 20px margin)
  const leftPosition = isCameraMinimapVisible ? '280px' : '70px';

  const dynamicStyle = {
    left: leftPosition
  };

  if (!isVisible) {
    return (
      <button
        className="keyguide-toggle-btn"
        onClick={onToggleVisibility}
        title={t.keyGuide.open}
        style={dynamicStyle}
      >
        ⌨️
      </button>
    )
  }

  return (
    <div className="keyguide-panel" style={dynamicStyle}>
      <div className="keyguide-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4>{t.keyGuide.title}</h4>
          <button
            className="keyguide-close-btn"
            onClick={onToggleVisibility}
            title={t.keyGuide.close}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="keyguide-content">
        <div className="guide-section">
          <div className="guide-title">{t.keyGuide.sections.cubeControl}</div>
          <div className="guide-keys">
            <div className="guide-row">
              <span className="guide-key">Q</span>
              <span className="guide-desc">{t.keyGuide.keys.frontRotate}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">W</span>
              <span className="guide-desc">{t.keyGuide.keys.upRotate}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">E</span>
              <span className="guide-desc">{t.keyGuide.keys.backRotate}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">A</span>
              <span className="guide-desc">{t.keyGuide.keys.leftRotate}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">S</span>
              <span className="guide-desc">{t.keyGuide.keys.downRotate}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">D</span>
              <span className="guide-desc">{t.keyGuide.keys.rightRotate}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">↑</span>
              <span className="guide-key">↓</span>
              <span style={{ color: '#888', fontSize: '11px', margin: '0 4px' }}>or</span>
              <span className="guide-key">I</span>
              <span className="guide-key">K</span>
              <span className="guide-desc">{t.keyGuide.keys.cubeTilt}</span>
            </div>
            <div className="guide-row">
              <span style={{ color: '#cccccc', fontSize: '12px' }}>Hold <span className="guide-key">Shift</span></span>
              <span className="guide-desc">{t.keyGuide.keys.counterClockwise}</span>
            </div>
          </div>
          <div style={{
            marginTop: '8px',
            padding: '6px 8px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
            fontSize: '10px',
            color: 'rgba(255, 255, 255, 0.7)'
          }}>
            {t.keyGuide.keys.counterClockwiseHint}
          </div>
        </div>

        <div className="guide-section">
          <div className="guide-title">{t.keyGuide.sections.camera}</div>
          <div className="guide-keys">
            <div className="guide-row">
              <span className="guide-key">←</span>
              <span style={{ color: '#888', fontSize: '11px', margin: '0 4px' }}>or</span>
              <span className="guide-key">J</span>
              <span className="guide-desc">{t.keyGuide.keys.prevCorner}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">→</span>
              <span style={{ color: '#888', fontSize: '11px', margin: '0 4px' }}>or</span>
              <span className="guide-key">L</span>
              <span className="guide-desc">{t.keyGuide.keys.nextCorner}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">T</span>
              <span className="guide-desc">{t.keyGuide.keys.topView}</span>
            </div>
          </div>
        </div>

        <div className="guide-section">
          <div className="guide-title">{t.keyGuide.sections.system}</div>
          <div className="guide-keys">
            <div className="guide-row">
              <span className="guide-key">Space</span>
              <span className="guide-desc">{t.keyGuide.keys.timerStartStop}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">Tab</span>
              <span className="guide-desc">{t.keyGuide.keys.modeSwitch}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">0</span>
              <span className="guide-desc">{t.keyGuide.keys.shuffle}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">R</span>
              <span className="guide-desc">{t.keyGuide.keys.reset}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">Ctrl+Z</span>
              <span className="guide-desc">{t.keyGuide.keys.undo}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">Ctrl+Y</span>
              <span className="guide-desc">{t.keyGuide.keys.redo}</span>
            </div>
          </div>
        </div>

        <div className="guide-section">
          <div className="guide-title">{t.keyGuide.sections.controls}</div>
          <div className="guide-keys">
            <div className="guide-row">
              <span className="guide-key">,</span>
              <span className="guide-desc">{t.keyGuide.keys.speedDecrease}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">.</span>
              <span className="guide-desc">{t.keyGuide.keys.speedIncrease}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">Shift+,</span>
              <span className="guide-desc">{t.keyGuide.keys.zoomOut}</span>
            </div>
            <div className="guide-row">
              <span className="guide-key">Shift+.</span>
              <span className="guide-desc">{t.keyGuide.keys.zoomIn}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="keyguide-scroll-hint">
        <span>{t.keyGuide.scrollHint}</span>
      </div>
    </div>
  )
}